<template>
  <div>
    <v-dialog :value="value" max-width="900" scrollable @input="$emit('input', $event)">
      <v-card>
        <v-card-title>{{ $t('spedilink.offers.myInterestsTitle') }}</v-card-title>
        <v-card-text>
          <v-progress-linear v-if="loading" indeterminate />
          <p v-else-if="!interests.length" class="mb-0">
            {{ $t('spedilink.offers.myInterestsEmpty') }}
          </p>
          <v-simple-table v-else>
            <thead>
              <tr>
                <th>{{ $t('spedilink.offers.myInterestsRoute') }}</th>
                <th>{{ $t('spedilink.offers.myInterestsContent') }}</th>
                <th>{{ $t('spedilink.offers.myInterestsSentAt') }}</th>
                <th>{{ $t('spedilink.offers.myInterestsReporter') }}</th>
                <th>{{ $t('spedilink.offers.myInterestsStatus') }}</th>
                <th>{{ $t('spedilink.offers.myInterestsContact') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in interests" :key="row.id">
                <td>
                  <div>{{ row.loadingPlace || '—' }}</div>
                  <div class="lightText--text">{{ row.unloadingPlace || '—' }}</div>
                </td>
                <td>{{ row.content || '—' }}</td>
                <td>{{ row.createdAt | dateFormat('DD.MM.YYYY HH:mm') }}</td>
                <td>{{ row.publisherName || '—' }}</td>
                <td>
                  <v-chip small :color="row.isRead ? 'success' : 'primary'" text-color="white">
                    {{ $t(row.isRead ? 'spedilink.offers.myInterestsStatusRead' : 'spedilink.offers.myInterestsStatusUnread') }}
                  </v-chip>
                </td>
                <td>
                  <div class="d-flex align-center">
                    <div
                      v-if="row.orderId"
                      class="d-flex align-center pointer mr-2"
                      @click.stop="openOfferDetails(row)"
                    >
                      <v-icon size="25" color="primary">$infoBox</v-icon>
                    </div>
                    <DataTablePopupCell
                      v-if="row.publisherPhone"
                      :phone="row.publisherPhone"
                      :click-type="ClickPhone"
                      :order-id="row.orderId"
                      class="mr-2"
                    />
                    <DataTablePopupCell
                      v-if="row.publisherEmail"
                      :email="row.publisherEmail"
                      :click-type="ClickEmail"
                      :subject-text="interestMailSubject(row)"
                      :order-id="row.orderId"
                    />
                    <span v-if="!row.publisherPhone && !row.publisherEmail && !row.orderId" class="lightText--text">—</span>
                  </div>
                </td>
              </tr>
            </tbody>
          </v-simple-table>
        </v-card-text>
        <v-card-actions class="justify-end">
          <v-btn color="primary" depressed @click="$emit('input', false)">
            {{ $t('common.close') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <OfferDetailsDialog
      v-if="selectedOfferId"
      :value="offerDetailsActive"
      :item-id="selectedOfferId"
      :is-vehicle="selectedIsVehicle"
      @closed="onOfferDetailsClosed"
    />
  </div>
</template>

<script>
import DataTablePopupCell from '@/components/DataTablePopupCell'
import OfferDetailsDialog from '@/views/Market/dialogs/OfferDetailsDialog.vue'
import Service from '@/services'
import { Phone as ClickPhone, Email as ClickEmail } from '@/types/click-order-types.js'

export default {
  name: 'MySpediLinkInterestsDialog',
  components: {
    DataTablePopupCell,
    OfferDetailsDialog
  },
  model: {
    prop: 'value',
    event: 'input'
  },
  props: {
    value: {
      type: Boolean,
      default: false
    },
    linkId: {
      type: String,
      default: null
    }
  },
  data () {
    return {
      loading: false,
      interests: [],
      ClickPhone,
      ClickEmail,
      offerDetailsActive: false,
      selectedOfferId: null,
      selectedIsVehicle: false
    }
  },
  watch: {
    value (opened) {
      if (opened) {
        this.fetchInterests()
      }
    }
  },
  methods: {
    openOfferDetails (row) {
      if (!row.orderId) {
        return
      }
      this.selectedOfferId = String(row.orderId)
      this.selectedIsVehicle = (row.orderType || '') === 'Vehicle'
      this.offerDetailsActive = true
    },
    onOfferDetailsClosed () {
      this.offerDetailsActive = false
      this.selectedOfferId = null
    },
    interestMailSubject (row) {
      const from = (row.loadingPlace || '').trim()
      const to = (row.unloadingPlace || '').trim()
      if (!from && !to) {
        return ''
      }
      if (!from) {
        return to
      }
      if (!to) {
        return from
      }
      return `${from} - ${to}`
    },
    mapInterest (i, idx) {
      const first = i?.publisherFirstName || i?.PublisherFirstName || ''
      const last = i?.publisherLastName || i?.PublisherLastName || ''
      const publisherName = [first, last].filter(Boolean).join(' ').trim()
      return {
        id: i?.interestId || i?.InterestId || i?.id || `my-int-${idx}`,
        orderId: i?.orderId || i?.OrderId || '',
        orderType: i?.orderType || i?.OrderType || '',
        loadingPlace: i?.loadingPlace || i?.LoadingPlace || '',
        unloadingPlace: i?.unloadingPlace || i?.UnloadingPlace || '',
        content: i?.content || i?.Content || '',
        createdAt: i?.createdAt || i?.CreatedAt,
        isRead: i?.isRead ?? i?.IsRead ?? false,
        publisherName,
        publisherEmail: i?.publisherEmail || i?.PublisherEmail || '',
        publisherPhone: i?.publisherPhone || i?.PublisherPhone || ''
      }
    },
    async fetchInterests () {
      this.interests = []
      this.loading = true
      try {
        const params = {}
        if (this.linkId) {
          params.linkId = this.linkId
        }
        const { data } = await Service.get('spedilink/SpediLinkOffers/GetMyInterests', params)
        const list = Array.isArray(data) ? data : (data?.results || data?.Results || [])
        this.interests = list.map((row, idx) => this.mapInterest(row, idx))
      } catch (e) {
        this.interests = []
      } finally {
        this.loading = false
      }
    }
  }
}
</script>
